import React, { useEffect, useState } from "react";
import "./InstallPWA.css";

const IOS_DISMISS_KEY_PREFIX = "iosModalDismissed";
const FALLBACK_DELAY_MS = 2000;

const getPortalDisplayName = () => {
  const portalTitle = localStorage.getItem("tituloWebPortal")?.trim();
  const clinicName = localStorage.getItem("nombreClinica")?.trim();

  return [portalTitle, clinicName].filter(Boolean).join(" - ") || "Portal Pacientes";
};

const getPortalStorageKey = (portalName) =>
  `${IOS_DISMISS_KEY_PREFIX}${portalName.replace(/[^a-z0-9]/gi, "")}`;

const buildManifest = (portalName) => ({
  name: portalName,
  short_name: portalName,
  description: `Acceso directo a ${portalName}`,
  start_url: "/",
  scope: "/",
  display: "standalone",
  background_color: "#ffffff",
  theme_color: "#ffffff",
  icons: [
    {
      src: "/assets/icon/pwa-192x192.png",
      sizes: "192x192",
      type: "image/png",
      purpose: "any maskable",
    },
    {
      src: "/assets/icon/pwa-512x512.png",
      sizes: "512x512",
      type: "image/png",
      purpose: "any maskable",
    },
  ],
});

const syncPwaMetadata = (portalName) => {
  const manifestLink = document.querySelector('link[rel="manifest"]');
  const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  const applicationNameMeta = document.querySelector('meta[name="application-name"]');
  const appleTouchIconLink = document.querySelector('link[rel="apple-touch-icon"]');

  let manifestUrl;

  if (manifestLink) {
    const manifestBlob = new Blob([JSON.stringify(buildManifest(portalName))], {
      type: "application/manifest+json",
    });
    manifestUrl = URL.createObjectURL(manifestBlob);
    manifestLink.setAttribute("href", manifestUrl);
  }

  if (appleTitleMeta) {
    appleTitleMeta.setAttribute("content", portalName);
  }

  if (applicationNameMeta) {
    applicationNameMeta.setAttribute("content", portalName);
  }

  if (appleTouchIconLink) {
    appleTouchIconLink.setAttribute("href", "/apple-touch-icon.png");
  }

  return () => {
    if (manifestUrl) {
      URL.revokeObjectURL(manifestUrl);
    }
  };
};

const InstallPWA = () => {
  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [portalName, setPortalName] = useState("Portal Pacientes");

  useEffect(() => {
    const resolvedPortalName = getPortalDisplayName();
    const releaseMetadata = syncPwaMetadata(resolvedPortalName);
    const standaloneMode =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;
    const userAgent = window.navigator.userAgent;
    const iosDevice =
      /iPad|iPhone|iPod/.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const androidDevice = /Android/i.test(userAgent);

    setPortalName(resolvedPortalName);
    setIsIOS(iosDevice);
    setIsAndroid(androidDevice);
    setIsStandalone(standaloneMode);

    if (standaloneMode) {
      return releaseMetadata;
    }

    let fallbackTimer;

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setShowModal(true);
    };

    const handleInstalled = () => {
      setDeferredPrompt(null);
      setShowModal(false);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    if (iosDevice) {
      const iosDismissKey = getPortalStorageKey(resolvedPortalName);
      if (!localStorage.getItem(iosDismissKey)) {
        fallbackTimer = window.setTimeout(() => setShowModal(true), FALLBACK_DELAY_MS);
      }
    } else if (androidDevice) {
      fallbackTimer = window.setTimeout(() => {
        setShowModal(true);
      }, FALLBACK_DELAY_MS);
    }

    return () => {
      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
      }
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      releaseMetadata();
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowModal(false);
  };

  const handleClose = () => {
    setShowModal(false);

    if (isIOS) {
      localStorage.setItem(getPortalStorageKey(portalName), "true");
    }
  };

  if (isStandalone || !showModal) {
    return null;
  }

  return (
    <div className="pwa-modal-overlay">
      <div className="pwa-modal" role="dialog" aria-modal="true" aria-labelledby="pwa-modal-title">
        <h2 id="pwa-modal-title">Crear Acceso Directo</h2>
        <p className="pwa-modal-lead">
          al {portalName}
          <br />
          para ingresar mas rapido desde tu dispositivo
        </p>

        {isIOS ? (
          <div className="pwa-instructions">
            <p>En iPhone o iPad:</p>
            <ol>
              <li>Toca Compartir en Safari o Chrome.</li>
              <li>Elige Agregar a pantalla de inicio.</li>
              <li>Toca Agregar para confirmar.</li>
            </ol>
          </div>
        ) : isAndroid && !deferredPrompt ? (
          <div className="pwa-instructions">
            <p>En Android, si no aparece el instalador automatico:</p>
            <ol>
              <li>Abre el menu de tres puntos del navegador.</li>
              <li>Elige Instalar o Agregar a pantalla principal.</li>
              <li>Confirma la instalacion.</li>
            </ol>
          </div>
        ) : (
          <div className="pwa-instructions">
            <p>Instala este acceso directo para entrar al portal mas rapido desde tu dispositivo.</p>
          </div>
        )}

        <div className="pwa-modal-buttons">
          {deferredPrompt ? (
            <button type="button" onClick={handleInstall} className="install-button">
              Instalar
            </button>
          ) : null}
          <button type="button" onClick={handleClose} className="close-button">
            {isIOS ? "Entendido" : "Ahora no"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
